/**
 * @module common/DualAxis/hooks/useAxisMetricOptions
 * @description 从 columnMetrics 派生指标下拉选项
 */

import { useCallback, useMemo } from 'react';
import {
  DEFAULT_TABLE_CODE,
  getMetricUid,
  resolveMetricFieldLabel,
  resolveMetricFieldValue,
} from '../axisMetricUtils';
import type { TAxisMetric, TMetricSelectOption } from '../types';

export function useAxisMetricOptions(
  columnMetrics: Record<string, unknown>[] | undefined,
) {
  const metricSelectOptions = useMemo<TMetricSelectOption[]>(() => {
    if (!columnMetrics?.length) return [];
    return columnMetrics
      .map((item) => {
        const metricValue = resolveMetricFieldValue(item);
        if (!metricValue) return null;
        const tableCode = String(item.table_code || DEFAULT_TABLE_CODE);
        const uid = getMetricUid(metricValue, tableCode);
        return {
          ...item,
          metricValue,
          value: uid,
          uid,
          text: resolveMetricFieldLabel(item, metricValue),
        } as TMetricSelectOption;
      })
      .filter((item): item is TMetricSelectOption => item != null);
  }, [columnMetrics]);

  const metricValues = useMemo(
    () => metricSelectOptions.map((item) => item.uid),
    [metricSelectOptions],
  );

  const metricPayloadMap = useMemo(
    () =>
      metricSelectOptions.reduce<Record<string, TAxisMetric>>((acc, item) => {
        if (item.uid) {
          acc[item.uid] = {
            metric: item.metricValue || '',
            tableCode: item.table_code || DEFAULT_TABLE_CODE,
          };
        }
        return acc;
      }, {}),
    [metricSelectOptions],
  );

  const filterSelectOptions = useMemo(
    () =>
      metricSelectOptions.map((item) => ({
        ...item,
        value: item.uid,
        text: item.text || '',
      })),
    [metricSelectOptions],
  );

  const selectOptions = useMemo(
    () =>
      metricSelectOptions.map((item) => ({
        value: item.metricValue,
        text: item.text || item.metricValue,
      })),
    [metricSelectOptions],
  );

  const getMetricsPayload = useCallback(
    (keys: string[]): TAxisMetric[] =>
      keys.map((key) => ({
        metric: metricPayloadMap[key]?.metric || '',
        tableCode: metricPayloadMap[key]?.tableCode || DEFAULT_TABLE_CODE,
      })),
    [metricPayloadMap],
  );

  const getMetricByUid = useCallback(
    (uid: string): string => metricPayloadMap[uid]?.metric || '',
    [metricPayloadMap],
  );

  return {
    metricSelectOptions,
    metricValues,
    metricPayloadMap,
    filterSelectOptions,
    selectOptions,
    getMetricsPayload,
    getMetricByUid,
  };
}
